import React from 'react';
import { Section } from './ui/Section';
import { Users, Network, Brain, Heart, ExternalLink, UserCheck, Sprout } from 'lucide-react';
export function About() {
  return <div className="space-y-32 relative" data-id="element-6">
      {/* Background Gradient Blob */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-[120px] -z-10" data-id="element-7" />

      {/* Background Section */}
      <Section id="about" className="relative" data-id="element-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24 items-center" data-id="element-9">
          <div className="space-y-8" data-id="element-10">
            <h2 className="text-4xl md:text-5xl font-bold leading-tight tracking-tight" data-id="element-11">
              Director of Engineering at{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300" data-id="element-12">
                LHV Bank
              </span>
              .
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed" data-id="element-13">
              I specialise in scaling high-performance engineering teams with a
              focus on sustainable growth, organisational health, and leveraging
              AI safely in regulated environments.
            </p>

            <div className="space-y-4 pt-4" data-id="element-14">
              <p className="text-sm text-muted-foreground" data-id="element-15">
                Previously at Sky Betting & Gaming, PokerStars, Ticket Arena
              </p>
              <a href="https://www.linkedin.com/in/macsdickinson/details/experience/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300 transition-colors" data-id="element-16">
                View full work history
                <ExternalLink size={14} data-id="element-17" />
              </a>
            </div>
          </div>

          <div className="relative" data-id="element-18">
            <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/20 to-purple-500/20 rounded-2xl blur-xl" data-id="element-19" />
            <div className="relative p-8 rounded-2xl bg-card/80 dark:bg-black/40 border border-border/50 dark:border-white/10 backdrop-blur-md space-y-6 shadow-lg" data-id="element-20">
              <h3 className="text-xl font-medium text-foreground" data-id="element-21">
                Ask me about
              </h3>
              <div className="space-y-4" data-id="element-22">
                {[{
                icon: Users,
                label: 'How to scale engineering teams without losing culture'
              }, {
                icon: Network,
                label: 'Restructuring orgs for better flow'
              }, {
                icon: Brain,
                label: 'Making AI actually useful in regulated industries'
              }, {
                icon: Heart,
                label: 'Mental health in the workplace'
              }].map((item, i) => <div key={i} className="flex items-center gap-4 p-3 rounded-lg hover:bg-secondary/50 dark:hover:bg-white/5 transition-colors group" data-id="element-23">
                    <div className="p-2 rounded-md bg-secondary/50 dark:bg-white/5 text-blue-400 group-hover:text-blue-300 transition-colors" data-id="element-24">
                      <item.icon size={20} data-id="element-25" />
                    </div>
                    <span className="text-muted-foreground group-hover:text-foreground transition-colors text-sm" data-id="element-26">
                      {item.label}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Philosophy Section */}
      <Section className="relative" data-id="element-27">
        <div className="absolute left-0 bottom-0 w-[400px] h-[400px] bg-purple-500/10 rounded-full blur-[100px] -z-10" data-id="element-28" />

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16" data-id="element-29">
          <div className="md:col-span-4" data-id="element-30">
            <h2 className="text-sm font-mono text-muted-foreground uppercase tracking-wider mb-4" data-id="element-31">
              Philosophy
            </h2>
            <h3 className="text-3xl font-medium text-foreground mb-6" data-id="element-32">
              How I Think
            </h3>
            <p className="text-muted-foreground" data-id="element-33">
              Principles that guide my decision making and leadership style.
            </p>
          </div>

          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-34">
            {[{
            title: 'Teams > Tools',
            desc: 'Culture eats strategy for breakfast. Tools are secondary to communication.',
            gradient: 'from-blue-500/20 to-cyan-500/20'
          }, {
            title: 'Measure Outcomes',
            desc: 'Activity is not progress. Focus on business value and customer impact.',
            gradient: 'from-purple-500/20 to-pink-500/20'
          }, {
            title: 'Sustainable Pace',
            desc: 'Burnout destroys more value than it creates. Long-term thinking wins.',
            gradient: 'from-emerald-500/20 to-teal-500/20'
          }, {
            title: 'AI as Leverage',
            desc: "AI isn't magic, it's a force multiplier for capable engineers.",
            gradient: 'from-orange-500/20 to-rose-500/20'
          }, {
            title: 'Humans First',
            desc: 'People are humans before they are employees. Respect who they are, not just what they do.',
            gradient: 'from-pink-500/20 to-purple-500/20'
          }, {
            title: 'Living Systems',
            desc: 'Organisations grow, adapt, and evolve. Static structures guarantee fragility.',
            gradient: 'from-cyan-500/20 to-blue-500/20'
          }].map((item, i) => <div key={i} className="group relative overflow-hidden p-6 rounded-xl bg-card/80 dark:bg-card/30 border border-border/50 dark:border-white/10 hover:border-border dark:hover:border-white/20 backdrop-blur-sm transition-all duration-300 shadow-sm hover:shadow-md" data-id="element-35">
                {/* Background Gradient on Hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${item.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} data-id="element-36" />

                <div className="relative z-10" data-id="element-37">
                  <h4 className="text-lg font-medium text-foreground mb-2 group-hover:text-blue-400 transition-colors" data-id="element-38">
                    {item.title}
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed" data-id="element-39">
                    {item.desc}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </Section>
    </div>;
}